import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './SearchModal.module.css';

const searchableData = [
  { id: 1, title: "Business Directory", description: "Find local community businesses", path: "/business", type: "Page" },
  { id: 2, title: "Notices & Announcements", description: "Latest updates from society", path: "/notices", type: "Page" },
  { id: 3, title: "Community Events", description: "Upcoming society events and festivals", path: "/events", type: "Page" },
  { id: 4, title: "Amenities", description: "Explore clubhouse, gym, and pool", path: "/amenities", type: "Page" },
  { id: 5, title: "Photo Gallery", description: "Memories from past events", path: "/gallery", type: "Page" },
  { id: 6, title: "Contact Us", description: "Society office and emergency contacts", path: "/contact", type: "Page" },
  { id: 7, title: "About Society", description: "Learn about Sanskriti Shubh Aangan", path: "/about", type: "Page" },
  { id: 8, title: "Diwali Mela 2026", description: "Community Hall - 10 Nov", path: "/events", type: "Event" },
  { id: 9, title: "Annual Sports Meet", description: "Play Ground - 15 Dec", path: "/events", type: "Event" },
  { id: 10, title: "General Meeting", description: "Monthly society meeting", path: "/events", type: "Event" },
  { id: 11, title: "Water Supply Notice", description: "Maintenance scheduled", path: "/notices", type: "Notice" },
  { id: 12, title: "Security Update", description: "New visitor policy", path: "/notices", type: "Notice" },
  { id: 13, title: "Sharma Digital Solutions", description: "Web development & IT consulting", path: "/business", type: "Business" },
  { id: 14, title: "Glow Studio", description: "Professional beauty and salon", path: "/business", type: "Business" }
];

const SearchModal = ({ isOpen, onClose }) => {
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'; // Prevent scrolling
      setQuery("");
      setResults([]);
      // Auto-focus the input when modal opens
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      document.body.style.overflow = 'unset';
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (query.trim().length > 0) {
      const lowerQuery = query.toLowerCase();
      const filtered = searchableData.filter(item => 
        item.title.toLowerCase().includes(lowerQuery) || 
        item.description.toLowerCase().includes(lowerQuery) ||
        item.type.toLowerCase().includes(lowerQuery)
      );
      setResults(filtered);
    } else {
      setResults([]);
    }
  }, [query]);

  if (!isOpen) return null;

  const quickLinks = [
    { name: "Business", path: "/business" },
    { name: "Events", path: "/events" },
    { name: "Notices", path: "/notices" },
    { name: "Amenities", path: "/amenities" },
    { name: "Gallery", path: "/gallery" }
  ];

  const handleResultClick = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.searchHeader}>
          <svg className={styles.searchIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input 
            type="text" 
            placeholder="Search events, notices, businesses, gallery..." 
            className={styles.searchInput}
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className={styles.escBtn} onClick={onClose}>Esc</button>
        </div>
        
        <div className={styles.searchBody}>
          {query.trim().length === 0 ? (
            <>
              <p className={styles.hintText}>Start typing to search society resources</p>
              <div className={styles.quickLinks}>
                {quickLinks.map((link) => (
                  <Link 
                    key={link.name} 
                    to={link.path} 
                    className={styles.quickLinkPill}
                    onClick={onClose}
                  >
                    {link.name}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="7" y1="17" x2="17" y2="7"></line>
                      <polyline points="7 7 17 7 17 17"></polyline>
                    </svg>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className={styles.resultsContainer}>
              {results.length > 0 ? (
                <ul className={styles.resultsList}>
                  {results.map(item => (
                    <li 
                      key={item.id} 
                      className={styles.resultItem}
                      onClick={() => handleResultClick(item.path)}
                    >
                      <div className={styles.resultIconWrapper}>
                        {item.type === 'Page' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>}
                        {item.type === 'Event' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>}
                        {item.type === 'Notice' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3zm-8.27 4a2 2 0 0 1-3.46 0"></path></svg>}
                        {item.type === 'Business' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>}
                      </div>
                      <div className={styles.resultContent}>
                        <h4 className={styles.resultTitle}>{item.title}</h4>
                        <p className={styles.resultDesc}>{item.description}</p>
                      </div>
                      <span className={styles.resultBadge}>{item.type}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className={styles.noResults}>
                  <p>No results found for "{query}"</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
